import type { ColumnDef } from "@tanstack/react-table";

import { Badge } from "@/components/ui/badge";
import { useTargetScoreQuery } from "@/hooks/queries";
import { TARGET_SCORE_OPTIONS, TestStatus } from "@/lib/constant";
import { cn } from "@/lib/utils";
import type { TestDateItem } from "@/types/common";

function isToday(dateStr: string) {
  return dateStr === new Date().toISOString().split("T")[0];
}

function StatusBadge({ exam }: { exam: TestDateItem["exam"] }) {
  const { data: targetScore = TARGET_SCORE_OPTIONS[2] } = useTargetScoreQuery();

  const status = exam?.status ?? TestStatus.PENDING;
  const score = exam?.score ?? 0;
  const isFailed = status === TestStatus.COMPLETED && score < targetScore;
  const isProgress = status === TestStatus.IN_PROGRESS;
  const isPending = status === TestStatus.PENDING;
  const variant = isFailed ? "destructive" : isProgress ? "outline" : "default";

  return (
    <Badge
      variant={variant}
      className={cn(
        "rounded-md sm:rounded w-16 h-7 font-semibold sm:static sm:top-auto sm:right-auto absolute top-4 right-4",
        isPending && "bg-muted text-muted-foreground",
      )}
    >
      {status === TestStatus.COMPLETED ? `${score}점` : isProgress ? "진행중" : "미제출"}
    </Badge>
  );
}

export function getTestDateColumns(): Array<ColumnDef<TestDateItem>> {
  return [
    {
      id: "category",
      header: "카테고리",
      cell: ({ row }) => (
        <span className="text-muted-foreground font-medium">{row.original.category ?? "일일 테스트"}</span>
      ),
    },
    {
      id: "title",
      header: "제목",
      cell: ({ row }) => {
        const today = isToday(row.original.date);

        return (
          <span className="text-left text-foreground font-semibold sm:font-medium text-base sm:text-sm">
            {today ? "오늘의 테스트" : `${row.original.date} 일일 테스트`}
          </span>
        );
      },
    },
    {
      id: "status",
      header: "상태",
      cell: ({ row }) => <StatusBadge exam={row.original.exam} />,
    },
  ];
}
